import { useCallback, useEffect, useRef, useState } from "react";
import {
  SpeechRecognitionService,
  SpeechRecognitionStatus,
  SpeechRecognitionError,
} from "../voice/speech-recognition";
import { parseIntent } from "../voice/intent-parser";
import { CommandRegistry } from "../voice/command-registry";
import { createSearchCommand } from "../voice/commands/search.command";
import { createNavigateCommand } from "../voice/commands/navigate.command";
import {
  PortalMessageToApp,
  usePortalProvider,
} from "../providers/portal-provider";

export interface VoiceNavigationState {
  status: SpeechRecognitionStatus;
  lastTranscript: string | null;
  lastError: SpeechRecognitionError | null;
  isSupported: boolean;
}

export interface VoiceNavigationActions {
  start: () => void;
  stop: () => void;
}

export type UseVoiceNavigationResult = VoiceNavigationState &
  VoiceNavigationActions;

const INITIAL_STATE: VoiceNavigationState = {
  status: SpeechRecognitionStatus.IDLE,
  lastTranscript: null,
  lastError: null,
  isSupported: false,
};

// Navega dentro do catalog usando o mesmo canal do header
// deste portal: posta `pushRoute` quando já está no catalog, senão recarrega
// para a rota completa. Mantém o comportamento de `handleCatalogClick`.
function navigateInCatalog(
  route: string,
  sendMessageToApp: (message: PortalMessageToApp) => void
): void {
  if (typeof window === "undefined") return;

  if (window.location.pathname.includes("/app/catalog")) {
    sendMessageToApp({ detail: { pushRoute: route } });
  } else {
    window.location.href = `/app/catalog${route === "/" ? "" : route}`;
  }
}

export function useVoiceNavigation(): UseVoiceNavigationResult {
  const { sendMessageToApp } = usePortalProvider();
  const [state, setState] = useState<VoiceNavigationState>(INITIAL_STATE);

  const recognitionRef = useRef<SpeechRecognitionService | null>(null);
  const registryRef = useRef<CommandRegistry | null>(null);

  const initRegistry = useCallback((): boolean => {
    if (!sendMessageToApp) return false;

    const registry = new CommandRegistry({
      onCommandNotFound: (parsed) => {
        setState((prev) => ({
          ...prev,
          lastTranscript: parsed.originalTranscript,
        }));
      },
      onCommandError: (_error, parsed) => {
        setState((prev) => ({
          ...prev,
          lastTranscript: parsed.originalTranscript,
          status: SpeechRecognitionStatus.ERROR,
        }));
      },
    });

    registry.register(createSearchCommand({ sendMessageToApp }));
    registry.register(
      createNavigateCommand({
        push: (route) => navigateInCatalog(route, sendMessageToApp),
      })
    );

    registryRef.current = registry;
    return true;
  }, [sendMessageToApp]);

  const initRecognition = useCallback(() => {
    const recognition = new SpeechRecognitionService({ lang: "pt-BR" });

    recognition.onStatusChange((status: SpeechRecognitionStatus) => {
      setState((prev) => ({ ...prev, status, lastError: null }));
    });

    recognition.onResult(async ({ transcript }: { transcript: string }) => {
      setState((prev) => ({ ...prev, lastTranscript: transcript }));

      if (!registryRef.current) return;

      const parsed = parseIntent(transcript);
      await registryRef.current.execute(parsed);
    });

    recognition.onError((error: SpeechRecognitionError) => {
      setState((prev) => ({
        ...prev,
        lastError: error,
        status: SpeechRecognitionStatus.ERROR,
      }));
    });

    recognitionRef.current = recognition;
    return recognition;
  }, []);

  useEffect(() => {
    const registryReady = initRegistry();

    if (!registryReady) {
      console.warn(
        "[useVoiceNavigation] sendMessageToApp unavailable — commands not registered"
      );
      return;
    }

    const recognition = initRecognition();

    setState((prev) => ({ ...prev, isSupported: recognition.isSupported() }));

    return () => {
      recognitionRef.current?.abort();
    };
  }, [initRecognition, initRegistry]);

  const start = useCallback(() => {
    recognitionRef.current?.start();
  }, []);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  return { ...state, start, stop };
}
